// ABOUTME: Shared component for displaying chore lists with completion toggles
// ABOUTME: Used by both parent dashboard and child dashboard for consistent UI

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  LinearProgress,
  Button,
  IconButton,
  Theme,
  Snackbar,
  Alert,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { ChoreListPeriodStatus, ChoreListType, ChoreListStatus } from '../types/api';
import { choresAPI } from '../services/api';
import { isChoreListApproved, normalizeRepeatFrequency, CHORE_LIST_DELAY_MS } from '../utils/choreListUtils';
import naluV1Theme from '../theme/theme.nalu-v1';

interface ChoreListDisplayProps {
  choreList: ChoreListPeriodStatus;
  onItemToggle: (choreListId: number, itemId: number, isCurrentlyComplete: boolean) => void;
  compact?: boolean; // Compact mode for parent dashboard
  isParentView?: boolean; // Whether the current user is a parent
  onRefresh?: () => void; // Callback to refresh lists after approval
  autoCollapse?: boolean; // When true, starts 5s countdown then collapses (Issue #1515)
  familyTimezone?: string; // Used for period-aware localStorage check
  onApprovalSuccess?: (choreListId: number) => void; // Called after successful approval (MakeItRight, ResetAndReflect, or Regular RequiresApproval)
  isReadOnly?: boolean; // When true, disables all completion actions (historical view)
  isHighlighted?: boolean; // When true, this is the list the approval-queue navigated to — force-expand it (issue #1907)
}

const getMakeItRightColor = (theme: Theme) => theme.palette.secondary.light;
const getResetAndReflectColor = (theme: Theme) => theme.palette.info.light;
const getOneOnOneTimeColor = (theme: Theme) => theme.palette.warning.light;

const ChoreListDisplay: React.FC<ChoreListDisplayProps> = ({
  choreList,
  onItemToggle,
  compact = false,
  isParentView = false,
  onRefresh,
  autoCollapse = false,
  onApprovalSuccess,
  isReadOnly = false,
  isHighlighted = false,
}) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const frequency = normalizeRepeatFrequency(choreList.repeatFrequency);
  // isApproved covers MakeItRight, ResetAndReflect, OneOnOneTime, and RequiresApproval regular lists
  const isApproved = isChoreListApproved(choreList);

  // isPendingApproval covers both list types
  const isPendingApproval = choreList.status === ChoreListStatus.PendingApproval;

  // Snackbar state for user feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [loading, setLoading] = useState(false);

  // Collapse state — persisted in localStorage per list ID
  const localStorageKey = `chore-list-collapsed-${choreList.id}`;
  // Auto-collapse key stores the periodEnd of the period when auto-collapse was triggered (Issue #1515)
  const autoCollapseKey = `chore-list-auto-collapsed-${choreList.id}`;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    // Check auto-collapse first: if stored periodEnd is in the future, start collapsed
    const storedAutoCollapse = localStorage.getItem(autoCollapseKey);
    if (storedAutoCollapse && new Date() < new Date(storedAutoCollapse)) {
      return true;
    }
    return localStorage.getItem(localStorageKey) === 'true';
  });

  // Timer ref for 5-second auto-collapse countdown (Issue #1515)
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start or cancel 5-second auto-collapse timer when autoCollapse prop changes (Issue #1515)
  useEffect(() => {
    if (autoCollapse) {
      autoCollapseTimerRef.current = setTimeout(() => {
        setIsCollapsed(true);
        localStorage.setItem(autoCollapseKey, choreList.periodEnd);
      }, CHORE_LIST_DELAY_MS);
    } else {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current);
        autoCollapseTimerRef.current = null;
      }
    }

    return () => {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current);
        autoCollapseTimerRef.current = null;
      }
    };
  }, [autoCollapse, autoCollapseKey, choreList.periodEnd]);

  // Force-expand when this becomes the approval-queue's navigation target, so the
  // itemized checklist is actually visible — collapsed state would otherwise hide it (issue #1907)
  useEffect(() => {
    if (isHighlighted && isCollapsed) {
      setIsCollapsed(false);
      localStorage.setItem(localStorageKey, 'false');
      localStorage.removeItem(autoCollapseKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlighted]);

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    localStorage.setItem(localStorageKey, String(next));
    // If expanding, clear auto-collapse so it won't restore on next page load
    if (!next) {
      localStorage.removeItem(autoCollapseKey);
    }
  };

  const handleApprove = async (choreListId: number) => {
    setLoading(true);
    try {
      await choresAPI.approveMakeItRightList(choreListId);
      setSnackbarMessage('List approved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      onApprovalSuccess?.(choreListId);
      // Refresh lists
      onRefresh?.();
    } catch (error) {
      console.error('Failed to approve list:', error);
      setSnackbarMessage('Failed to approve list');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        mb: compact ? 1.5 : 2,
        p: compact ? 1 : 2,
        border: '1px solid',
        borderColor: choreList.isComplete ? 'success.main' : 'divider',
        // bare numbers multiply against theme.shape.borderRadius (16 for nalu-v1),
        // which produced an oversized 16-32px radius here (issue #1900)
        borderRadius: isNaluV1Theme ? '14px' : (compact ? 1 : 2),
        bgcolor: choreList.isComplete
          ? 'success.light'
          : choreList.type === ChoreListType.MakeItRight
            ? (theme) => `${getMakeItRightColor(theme)}15`
            : choreList.type === ChoreListType.ResetAndReflect
              ? (theme) => `${getResetAndReflectColor(theme)}15`
              : choreList.type === ChoreListType.OneOnOneTime
                ? (theme) => `${getOneOnOneTimeColor(theme)}15`
                : 'background.paper',
        borderLeft: choreList.type === ChoreListType.MakeItRight
          ? (theme) => `4px solid ${getMakeItRightColor(theme)}`
          : choreList.type === ChoreListType.ResetAndReflect
            ? (theme) => `4px solid ${getResetAndReflectColor(theme)}`
            : choreList.type === ChoreListType.OneOnOneTime
              ? (theme) => `4px solid ${getOneOnOneTimeColor(theme)}`
              : 'none',
      }}
    >
      {/* Header: Chevron toggle + Title and chips */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 0.5 : 1, cursor: 'pointer' }}
        onClick={handleToggleCollapse}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleCollapse();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={isCollapsed ? `Expand ${choreList.title}` : `Collapse ${choreList.title}`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, flexGrow: 1 }}>
          <IconButton
            size="small"
            aria-hidden={true}
            sx={{ p: 0 }}
            tabIndex={-1}
          >
            {isCollapsed ? (
              <ChevronRightIcon fontSize={compact ? 'small' : 'medium'} />
            ) : (
              <ExpandMoreIcon fontSize={compact ? 'small' : 'medium'} />
            )}
          </IconButton>
          <Typography
            variant={compact ? 'body2' : 'h6'}
            // variant is dynamic (body2 baseline 400 vs h6 baseline 500); 500 is bolding for body2 but redundant for h6, so it can't be classified as a single redundant/bolding case
            sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1 }}
          >
            {choreList.isComplete && <span>✓</span>}
            {choreList.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {isCollapsed && (
            <Chip
              label={`${choreList.completedItems}/${choreList.totalItems}`}
              size="small"
              variant="outlined"
              sx={compact ? { height: 20, fontSize: '0.7rem' } : undefined}
            />
          )}
          <Chip
            label={frequency}
            size="small"
            sx={compact ? { height: 20, fontSize: '0.7rem' } : undefined}
            color={frequency === 'Daily' ? 'primary' : frequency === 'Weekly' ? 'secondary' : 'default'}
          />
          {choreList.requiredForHeart && (
            <Chip
              label={compact ? '♥' : 'Required for Heart'}
              size="small"
              sx={compact
                ? { height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'white', color: 'error.main' }
                : { fontWeight: 600, bgcolor: 'white', color: 'error.main' }}
            />
          )}
          {isPendingApproval && (
            <Chip
              label="Waiting for parent approval"
              size="small"
              sx={compact
                ? { height: 20, fontSize: '0.7rem', bgcolor: 'warning.light', color: 'warning.contrastText' }
                : { bgcolor: 'warning.light', color: 'warning.contrastText' }}
            />
          )}
          {isApproved && (
            <Chip
              icon={
                <Box
                  component="img"
                  src="/nalu/paulele-256.png"
                  alt=""
                  data-testid="approved-chip-icon"
                  sx={{ width: 14, height: 14, objectFit: 'contain' }}
                />
              }
              label="Approved"
              size="small"
              sx={compact
                ? { height: 20, fontSize: '0.7rem', bgcolor: 'white', color: 'success.main', fontWeight: 600 }
                : { bgcolor: 'white', color: 'success.main', fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: compact ? 0.5 : 1 }}>
        <LinearProgress
          variant="determinate"
          value={choreList.totalItems > 0 ? (choreList.completedItems / choreList.totalItems) * 100 : 0}
          color={choreList.isComplete ? 'success' : 'primary'}
          sx={{ height: compact ? 4 : 8, borderRadius: compact ? 2 : 4 }}
        />
        <Collapse in={!isCollapsed} unmountOnExit>
          {!compact && (
            <Typography variant="caption" color="text.secondary">
              {choreList.completedItems} of {choreList.totalItems} complete
            </Typography>
          )}
        </Collapse>
      </Box>

      {/* Chore items list — animated collapse */}
      <Collapse in={!isCollapsed} unmountOnExit>
        <List dense disablePadding>
          {choreList.items.map((item) => (
            <ListItem
              key={item.id}
              dense
              disablePadding
              sx={{
                py: compact ? 0.25 : 0.5,
                px: 0.5,
                cursor: isApproved || isReadOnly ? 'default' : 'pointer',
                '&:hover': isApproved || isReadOnly ? {} : { bgcolor: 'action.hover' },
                borderRadius: isNaluV1Theme ? '8px' : 1,
              }}
              onClick={isApproved || isReadOnly ? undefined : () => onItemToggle(choreList.id, item.id, item.isCompleted)}
            >
              <ListItemIcon sx={{ minWidth: compact ? 32 : 40 }}>
                <Checkbox
                  edge="start"
                  checked={item.isCompleted}
                  tabIndex={-1}
                  disableRipple
                  color="success"
                  size={compact ? 'small' : 'medium'}
                  disabled={isApproved || isReadOnly}
                />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  variant: compact ? 'body2' : 'body1',
                  sx: {
                    textDecoration: item.isCompleted ? 'line-through' : 'none',
                    color: item.isCompleted ? 'text.secondary' : 'text.primary',
                    fontSize: compact ? '0.85rem' : undefined,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>

      {/* Approval button for lists requiring parent approval */}
      {isPendingApproval &&
       isParentView &&
       !isReadOnly &&
       (choreList.type === ChoreListType.MakeItRight || choreList.type === ChoreListType.ResetAndReflect || choreList.type === ChoreListType.OneOnOneTime || choreList.requiresApproval) && (
        <Button
          data-approval-button={choreList.type === ChoreListType.MakeItRight ? true : undefined}
          data-reset-and-reflect-approval-button={choreList.type === ChoreListType.ResetAndReflect ? true : undefined}
          data-one-on-one-time-approval-button={choreList.type === ChoreListType.OneOnOneTime ? true : undefined}
          data-responsibilities-approval-button={choreList.type === ChoreListType.Regular ? true : undefined}
          data-testid={
            choreList.type === ChoreListType.MakeItRight ? "approve-make-it-right-button" :
            choreList.type === ChoreListType.ResetAndReflect ? "approve-reset-and-reflect-button" :
            choreList.type === ChoreListType.OneOnOneTime ? "approve-one-on-one-time-button" :
            "approve-responsibilities-button"
          }
          variant="contained"
          color={
            choreList.type === ChoreListType.MakeItRight ? "secondary" :
            choreList.type === ChoreListType.ResetAndReflect ? "info" :
            "primary"
          }
          onClick={() => handleApprove(choreList.id)}
          aria-label={
            choreList.type === ChoreListType.MakeItRight ? "Approve Make It Right list" :
            choreList.type === ChoreListType.ResetAndReflect ? "Approve Reset and Reflect list" :
            choreList.type === ChoreListType.OneOnOneTime ? "Approve 1:1 Time list" :
            "Approve responsibilities list"
          }
          sx={{ mt: 2 }}
          disabled={loading}
        >
          {loading ? 'Approving...' : 'Approve'}
        </Button>
      )}

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChoreListDisplay;
